<template>
  <div class="user-profile">
    <h1>Benutzerprofil</h1>

    <div v-if="!user" class="loading">
      <p>Benutzerdaten werden geladen...</p>
    </div>

    <div v-else class="profile-container">
      <div v-if="!isEditing && !isDeleting && !changingPassword" class="profile-view">
        <div class="profile-field">
          <label>Name:</label>
          <p>{{ user.name || '-' }}</p>
        </div>

        <div class="profile-field">
          <label>E-Mail:</label>
          <p>{{ user.email || '-' }}</p>
        </div>

        <div class="profile-field">
          <label>Telefon:</label>
          <p>{{ user.phone || '-' }}</p>
        </div>

        <div class="profile-field">
          <label>Adresse:</label>
          <p>{{ user.address || '-' }}</p>
        </div>

        <div class="profile-field">
          <label>Username:</label>
          <p>{{ user.username }}</p>
        </div>

        <div class="button-group">
          <button @click="isEditing = true" class="btn-edit">Bearbeiten</button>
          <button @click="logout" class="btn-logout">Abmelden</button>
        </div>

        <div class="secondary-actions">
          <button @click="changingPassword = true" class="btn-small">Passwort ändern</button>
          <button @click="deleteUser" class="btn-small-danger">Konto löschen</button>
        </div>
      </div>

      <div v-else-if="isEditing" class="profile-edit">
        <form @submit.prevent="saveChanges">
          <div class="form-group">
            <label for="username">Username:</label>
            <input type="text" id="username" v-model="editData.username" maxlength="50" required />
          </div>

          <div class="form-group">
            <label for="name">Name:</label>
            <input type="text" id="name" v-model="editData.name" />
          </div>

          <div class="form-group">
            <label for="email">E-Mail:</label>
            <input type="email" id="email" v-model="editData.email" />
          </div>

          <div class="form-group">
            <label for="phone">Telefon:</label>
            <input type="tel" id="phone" v-model="editData.phone" />
          </div>

          <div class="form-group">
            <label for="address">Adresse:</label>
            <input type="text" id="address" v-model="editData.address" />
          </div>

          <div class="button-group">
            <button type="submit" class="btn-save">Speichern</button>
            <button type="button" @click="cancelEdit" class="btn-cancel">Abbrechen</button>
          </div>
        </form>
      </div>

      <div v-if="isDeleting" class="profile-delete">
        <p class="warning-text">Sind Sie sicher? Dies kann nicht rückgängig gemacht werden.</p>
        <div class="button-group">
          <button @click="confirmDelete" class="btn-confirmDelete">Ja, löschen</button>
          <button @click="isDeleting = false" class="btn-cancelDelete">Abbrechen</button>
        </div>
      </div>

      <div v-if="changingPassword" class="profile-change-password">
        <form @submit.prevent="changePassword">
          <div class="form-group">
            <label for="currentPassword">Aktuelles Passwort:</label>
            <input type="password" id="currentPassword" v-model="currentPassword" required />
          </div>

          <div class="form-group">
            <label for="newPassword">Neues Passwort:</label>
            <input type="password" id="newPassword" v-model="newPassword" required />
          </div>

          <div class="form-group">
            <label for="confirmNewPassword">Bestätigen:</label>
            <input type="password" id="confirmNewPassword" v-model="confirmNewPassword" required />
          </div>

          <div class="button-group">
            <button type="submit" class="btn-save">Ändern</button>
            <button type="button" @click="changingPassword = false" class="btn-cancel">Abbrechen</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getUserByUsername,
  updateUserByUsername,
  updateUserPassword,
  deleteUserByUsername
} from '../services/dbApi';

export default {
  name: 'UserProfile',
  data() {
    return {
      user: null,
      isEditing: false,
      isDeleting: false,
      changingPassword: false,
      editData: {
        name: '',
        email: '',
        phone: '',
        address: '',
        username: ''
      },
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: ''
    };
  },
  mounted() {
    this.loadUserData();
  },
  methods: {
    sha256Fallback(input) {
      function rightRotate(value, amount) {
        return (value >>> amount) | (value << (32 - amount));
      }

      const words = [];
      const asciiBitLength = input.length * 8;

      for (let index = 0; index < input.length; index++) {
        words[index >> 2] |= (input.charCodeAt(index) & 0xff) << (24 - (index % 4) * 8);
      }

      words[input.length >> 2] |= 0x80 << (24 - (input.length % 4) * 8);
      while ((words.length % 16) !== 14) words.push(0);
      words.push(Math.floor(asciiBitLength / 0x100000000));
      words.push(asciiBitLength & 0xffffffff);

      const hash = [
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
        0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19
      ];

      const roundConstants = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2
      ];

      for (let offset = 0; offset < words.length; offset += 16) {
        const w = words.slice(offset, offset + 16);
        for (let t = 16; t < 64; t++) {
          const s0 = (rightRotate(w[t - 15], 7) ^ rightRotate(w[t - 15], 18) ^ (w[t - 15] >>> 3)) >>> 0;
          const s1 = (rightRotate(w[t - 2], 17) ^ rightRotate(w[t - 2], 19) ^ (w[t - 2] >>> 10)) >>> 0;
          w[t] = (w[t - 16] + s0 + w[t - 7] + s1) >>> 0;
        }

        let a = hash[0];
        let b = hash[1];
        let c = hash[2];
        let d = hash[3];
        let e = hash[4];
        let f = hash[5];
        let g = hash[6];
        let h = hash[7];

        for (let t = 0; t < 64; t++) {
          const s1 = (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) >>> 0;
          const choose = ((e & f) ^ (~e & g)) >>> 0;
          const temp1 = (h + s1 + choose + roundConstants[t] + w[t]) >>> 0;
          const s0 = (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) >>> 0;
          const majority = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
          const temp2 = (s0 + majority) >>> 0;

          h = g;
          g = f;
          f = e;
          e = (d + temp1) >>> 0;
          d = c;
          c = b;
          b = a;
          a = (temp1 + temp2) >>> 0;
        }

        hash[0] = (hash[0] + a) >>> 0;
        hash[1] = (hash[1] + b) >>> 0;
        hash[2] = (hash[2] + c) >>> 0;
        hash[3] = (hash[3] + d) >>> 0;
        hash[4] = (hash[4] + e) >>> 0;
        hash[5] = (hash[5] + f) >>> 0;
        hash[6] = (hash[6] + g) >>> 0;
        hash[7] = (hash[7] + h) >>> 0;
      }

      return hash.map(value => value.toString(16).padStart(8, '0')).join('');
    },
    async loadUserData() {
      try {
        const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
        const username = sessionStorage.getItem('username');

        if (!isLoggedIn || !username) {
          this.$router.push('/login');
          return;
        }

        const user = await getUserByUsername(username);

        if (user) {
          this.user = user;
          this.editData = { ...this.user };
        } else {
          console.error('Fehler beim Laden der Benutzerdaten');
        }
      } catch (e) {
        console.error(e);
      }
    },
    async saveChanges() {
      try {
        const username = sessionStorage.getItem('username');
        const requestedUsername = (this.editData.username || '').trim();
        if (!requestedUsername) {
          alert('Username ist erforderlich.');
          return;
        }

        const updatedUser = await updateUserByUsername(username, {
          username: requestedUsername,
          name: this.editData.name,
          phone: this.editData.phone,
          address: this.editData.address,
          email: this.editData.email
        });

        if (updatedUser) {
          if (updatedUser.username && updatedUser.username !== username) {
            sessionStorage.setItem('username', updatedUser.username);
            window.dispatchEvent(new Event('auth-changed'));
          }
          this.user = updatedUser;
          this.editData = { ...this.user };
          this.isEditing = false;
          alert('Änderungen gespeichert!');
        }
      } catch (e) {
        console.error(e);
        alert('Fehler: ' + (e?.message || 'Speichern fehlgeschlagen'));
      }
    },
    cancelEdit() {
      this.editData = { ...this.user };
      this.isEditing = false;
    },
    async logout() {
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('username');
      sessionStorage.removeItem('isAdmin');

      window.dispatchEvent(new Event('auth-changed'));

      this.isEditing = false;
      this.isDeleting = false;
      this.changingPassword = false;

      this.$router.push('/');
    },
    async deleteUser() {
      this.isEditing = false;
      this.isDeleting = true;
      this.changingPassword = false;
    },
    async confirmDelete() {
      try {
        const username = sessionStorage.getItem('username');
        await deleteUserByUsername(username);
        alert('Benutzer gelöscht.');
        this.logout();
      } catch (e) {
        alert('Fehler: ' + (e?.message || 'Netzwerkfehler'));
      }
    },
    async changePassword() {
      if (this.newPassword !== this.confirmNewPassword) {
        alert('Passwörter stimmen nicht überein.');
        return;
      }

      const currentHash = await this.sha256(this.currentPassword);
      const newHash = await this.sha256(this.newPassword);

      const username = sessionStorage.getItem('username');

      try {
        await updateUserPassword(username, currentHash, newHash);
        this.currentPassword = this.newPassword = this.confirmNewPassword = '';
        this.changingPassword = false;
        alert('Passwort geändert!');
      } catch (err) {
        alert('Fehler: ' + (err?.message || 'Passwort konnte nicht geändert werden'));
      }
    },
    async sha256(password) {
      if (!window.crypto || !window.crypto.subtle) {
        return this.sha256Fallback(password);
      }
      const msgBuffer = new TextEncoder().encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
  }
};
</script>

<style scoped>
.user-profile {
  font-family: var(--body-font-family, 'Inter', sans-serif);
  text-align: center;
  color: #2c3e50;
  margin-top: 40px;
}

.profile-container {
  max-width: 500px;
  margin: 0 auto;
  padding: 30px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  background: white;
}

.loading {
  padding: 20px;
}

.profile-field {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}

.profile-field label {
  font-weight: bold;
  color: #555;
}

.profile-field p {
  margin: 0;
  color: #333;
}

.form-group {
  margin-bottom: 15px;
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 0.9rem;
}

.form-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-top: 25px;
}

.secondary-actions {
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
  padding-top: 15px;
}

button {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.2s;
}

button:hover {
  opacity: 0.9;
}

.btn-edit, .btn-save { background-color: #42b983; color: white; }
.btn-logout, .btn-cancel { background-color: #e74c3c; color: white; }
.btn-confirmDelete { background-color: #c0392b; color: white; }
.btn-cancelDelete { background-color: #95a5a6; color: white; }

.btn-small {
  font-size: 0.8rem;
  background: none;
  color: #42b983;
  padding: 5px;
  text-align: left;
}

.btn-small-danger {
  font-size: 0.8rem;
  background: none;
  color: #e74c3c;
  padding: 5px;
  text-align: right;
}

.warning-text {
  color: #e74c3c;
  font-weight: bold;
}
</style>
